import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface PiopiyCredentials {
  userId: string;
  password: string;
  server: string;
}

interface PiopiyState {
  credentials: PiopiyCredentials | null;
  isLoggedIn: boolean;
  loginLoading: boolean;
  sdkStatus: "checking" | "available" | "unavailable";
  currentCall: {
    number: string;
    startTime: number;
    jobseekerData?: any;
  } | null;
  callStatus: "idle" | "calling" | "ringing" | "connected" | "onHold" | "muted";
  callDuration: number;
  isMuted: boolean;
  isOnHold: boolean;
  isMinimized: boolean;
  status: string;
  callCandidate: any | null;
}

const initialState: PiopiyState = {
  credentials: null,
  isLoggedIn: false,
  loginLoading: false,
  sdkStatus: "checking",
  currentCall: null,
  callStatus: "idle",
  callDuration: 0,
  isMuted: false,
  isOnHold: false,
  isMinimized: false,
  status: "Not logged in",
  callCandidate: null,
};

const piopiySlice = createSlice({
  name: "piopiy",
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<PiopiyCredentials>) => {
      state.credentials = action.payload;
    },
    clearCredentials: (state) => {
      state.credentials = null;
    },
    setLoggedIn: (state, action: PayloadAction<boolean>) => {
      state.isLoggedIn = action.payload;
    },
    setLoginLoading: (state, action: PayloadAction<boolean>) => {
      state.loginLoading = action.payload;
    },
    setSdkStatus: (
      state,
      action: PayloadAction<"checking" | "available" | "unavailable">
    ) => {
      state.sdkStatus = action.payload;
    },
    setCurrentCall: (
      state,
      action: PayloadAction<{
        number: string;
        startTime: number;
        jobseekerData?: any;
      } | null>
    ) => {
      state.currentCall = action.payload;
    },
    setCallStatus: (
      state,
      action: PayloadAction<
        "idle" | "calling" | "ringing" | "connected" | "onHold" | "muted"
      >
    ) => {
      state.callStatus = action.payload;
    },
    setCallDuration: (state, action: PayloadAction<number>) => {
      state.callDuration = action.payload;
    },
    setIsMuted: (state, action: PayloadAction<boolean>) => {
      state.isMuted = action.payload;
    },
    setIsOnHold: (state, action: PayloadAction<boolean>) => {
      state.isOnHold = action.payload;
    },
    setIsMinimized: (state, action: PayloadAction<boolean>) => {
      state.isMinimized = action.payload;
    },
    setStatus: (state, action: PayloadAction<string>) => {
      state.status = action.payload;
    },
    setCallCandidate: (state, action: PayloadAction<any | null>) => {
      state.callCandidate = action.payload;
    },
    resetPiopiyState: (state) => {
      state.isLoggedIn = false;
      state.loginLoading = false;
      state.currentCall = null;
      state.callStatus = "idle";
      state.callDuration = 0;
      state.isMuted = false;
      state.isOnHold = false;
      state.isMinimized = false;
      state.status = "Not logged in";
      state.callCandidate = null;
    },
    logout: (state) => {
      state.credentials = null;
      state.isLoggedIn = false;
      state.loginLoading = false;
      state.currentCall = null;
      state.callStatus = "idle";
      state.callDuration = 0;
      state.isMuted = false;
      state.isOnHold = false;
      state.isMinimized = false;
      state.status = "Not logged in";
      state.callCandidate = null;
    },
  },
});

export const {
  setCredentials,
  clearCredentials,
  setLoggedIn,
  setLoginLoading,
  setSdkStatus,
  setCurrentCall,
  setCallStatus,
  setCallDuration,
  setIsMuted,
  setIsOnHold,
  setIsMinimized,
  setStatus,
  setCallCandidate,
  resetPiopiyState,
  logout,
} = piopiySlice.actions;

export default piopiySlice.reducer;
